<script>
import {money} from "../../../core/forms/money";
import {storage} from "../../../core/utils/storage";
import {urlEncoder} from "../../../core/utils/url";
import Data from "../../../core/data/Data";
import Form from "../../../core/forms/form";
import AdCarousel from "../../../components/core/AdCarousel.vue";
import {validity} from "../../../core/forms/validity";
import {appDataMixin, paymentMethods as enabledPaymentMethods, systems} from "../../../core/data/global";
import {createBiDirectionalObject} from "../../../core/utils/bi-directional-obj";
import ConsumerLookup from "./ConsumerLookup.vue";
import axios from "axios";

const paymentMethods = createBiDirectionalObject({
    'ecocash': 'ZWG',
    'ecocash-usd':'USD',
    'stripe': 'USD',
})


const DONATION_CORPORATES = {
    226: {
        targetAmount: 2800,
        currency: 'USD'
    },
    241: {
        targetAmount: 10000,
        currency: 'USD'
    },
    323: {
        targetAmount: 3700,
        currency: 'USD'
    }
};


export default {
    name: "PayBillers",
    mixins: [storage, urlEncoder, money, validity, appDataMixin],
    components: {ConsumerLookup, AdCarousel, PhoneInput: () => import('../../../components/custom/phone-input.vue')},
    metaInfo() {
        return {
            title: (this.biller.name || 'Purchase')
        }
    },

    data: () => ({
        bills: new Data({
            url: '/billers/paylist',
            prefix: 'list'
        }),
        form: new Form({
            'biller_id': '',
            'email': '',
            'phone': '',
            'name': '',
            'ecocash': '',
            'surname': '',
            'message': '',
            'payment': '',
            'reference': '',
            'amount': '',
        }),
        isSticky: false,
        isAbsolute: false,
        termsAccepted: false,
        disableManualEntry: false,
        hasLookup: false,
        methods: {...paymentMethods},
        billerPaymentSettings: null,
        feeVariables: null,
        transactionFees: 0,
        originalAmount: '',
        isLoading: true,
        DONATION_CORPORATES: DONATION_CORPORATES,

        donationProgress: {
            amountCollected: 0,
            targetAmount: 0,
            currency: 'USD'
        }
    }),
    computed: {
        biller() {
            if (!this.bills?.content?.data) {
                return {};
            }
            const biller = this.bills.content.data[0] || {};
            return biller;
        },

        showDonationProgress() {
            if (!this.biller.corporate_id) {
                return false;
            }

            return this.DONATION_CORPORATES.hasOwnProperty(this.biller.corporate_id);
        },

        donationProgressPercentage() {
            if (!this.donationProgress.targetAmount || this.donationProgress.targetAmount === 0) {
                return 0;
            }
            const percentage = (this.donationProgress.amountCollected / this.donationProgress.targetAmount) * 100;
            return Math.min(percentage, 100);
        },
        paymentOptions() {
            if (!this.biller || !this.biller.bank_currencies) {
                return [];
            }
            const options = [];
            if (this.isEcocashZwgEnabled && this.biller.bank_currencies.includes('ZWG')) {
                options.push({ value: 'ecocash', label: 'EcoCash (ZWG)' });
            }
            if (this.isEcocashUsdEnabled && this.biller.bank_currencies.includes('USD')) {
                options.push({ value: 'ecocash-usd', label: 'EcoCash (USD)' });
            }
            if (this.isStripeEnabled && this.biller.bank_currencies.includes('USD')) {
                options.push({ value: 'stripe', label: 'International Card' });
            }
            return options;
        },
        isEcocashZwgEnabled() {
            return systems.ecocash_payment && systems.ecocash_zwg_payment;
        },
        isEcocashUsdEnabled() {
            return systems.ecocash_payment && systems.ecocash_usd_payment;
        },
        isEcocashEnabled() {
            return this.isEcocashZwgEnabled || this.isEcocashUsdEnabled;
        },
        isStripeEnabled() {
            return enabledPaymentMethods.includes('stripe');
        },
        submit() {
            this.form.errors.clear();
            this.form.biller_id =  this.decode(this.$route.params.id);
            this.form.amount = Number(this.originalAmount) + Number(this.transactionFees);
            this.purchase()
        },
    },
    methods: {
        formatDonationAmount(value) {
            const n = Number(value) || 0;
            return n.toLocaleString(undefined, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
        },
        async getBillerBills(billerId) {
            try {
                this.isLoading = true;
                this.bills.filters.id = billerId;
                await this.bills.fetch();

                if (this.showDonationProgress && this.biller.wallet_balance !== undefined) {
                    const corporateId = this.biller.corporate_id;
                    const donationConfig = this.DONATION_CORPORATES[corporateId];

                    if (donationConfig) {
                        this.donationProgress.amountCollected = this.biller.wallet_balance;
                        this.donationProgress.targetAmount = donationConfig.targetAmount;
                        this.donationProgress.currency = donationConfig.currency;
                    }
                }

                if (this.bills.content.data[0]?.corporate_id) {
                    await this.fetchBillerFees();
                }
            } catch (error) {
                this.isLoading = false;
            }
        },
        triggerLookup() {
            if (!this.disableManualEntry) {
                this.hasLookup = true;
            }
        },
        purchase() {
            this.saveToLocalStorage();
            return this.form.submit('/billers/payment').then(async response => {
                if (response.data.success) {
                    if (response.data.body.payment.payment === "ecocash" || response.data.body.payment.payment === "ecocash-usd" ) {
                        await this.$router.push(`/billers/ecocash?id=${response.data.body.payment.ref}`);
                    }

                    if (response.data.body.payment.payment === "stripe") {
                        window.location.href = response.data.body.payment.url;
                    }
                }
            })
        },
        handleScroll() {
            if (!this.$refs.stickyBlock?.$el || !this.$refs.contentBlock?.$el) {
                return;
            }

            const stickyBlock = this.$refs.stickyBlock.$el;
            const contentBlock = this.$refs.contentBlock.$el;

            if (window.scrollY > stickyBlock.offsetTop) {
                if (window.scrollY > contentBlock.offsetTop - stickyBlock.clientHeight) {
                    this.isSticky = false;
                    this.isAbsolute = true;
                } else {
                    this.isSticky = true;
                    this.isAbsolute = false;
                }
            } else {
                this.isSticky = false;
                this.isAbsolute = false;
            }
        },
        async fetchBillerFees() {
            try {
                if (!this.biller?.corporate_id) {
                    return;
                }

                const response = await axios.get(`/billers/${this.biller.corporate_id}/consumer-payment-settings`);

                if (!response?.data) {
                    return;
                }

                this.billerPaymentSettings = response.data.settings || null;
                this.feeVariables = response.data.fee_variables || null;

                if (!this.billerPaymentSettings || !this.feeVariables) {
                    return;
                }

                this.calculateTransactionFees();
            } catch (error) {
                this.billerPaymentSettings = null;
                this.feeVariables = null;
                this.transactionFees = 0;
            }
        },
        calculateTransactionFees() {
            if (!this.billerPaymentSettings || !this.feeVariables) {
                this.transactionFees = 0;
                return;
            }

            if (!this.originalAmount || !this.form.payment) {
                this.transactionFees = 0;
                return;
            }

            let total = 0;
            const payment = this.form.payment;
            const amount = parseFloat(this.originalAmount || 0);

            const isUSD = payment === 'ecocash-usd' || payment === 'stripe';

            if (isUSD && this.billerPaymentSettings.pass_fixed_fee_usd) {
                const fixedFee = parseFloat(this.feeVariables['BILLER-FIXED-FEE-USD']?.value || 0);
                total += fixedFee;
            }
            if (!isUSD && this.billerPaymentSettings.pass_fixed_fee_zwg) {
                const fixedFee = parseFloat(this.feeVariables['BILLER-FIXED-FEE-ZWG']?.value || 0);
                total += fixedFee;
            }

            if (isUSD && this.billerPaymentSettings.pass_service_fee_usd) {
                const serviceFee = (parseFloat(this.feeVariables['BILLER-SERVICE-FEE']?.value || 0) / 100) * amount;
                total += serviceFee;
            }
            if (!isUSD && this.billerPaymentSettings.pass_service_fee_zwg) {
                const serviceFee = (parseFloat(this.feeVariables['BILLER-SERVICE-FEE-ZWG']?.value || 0) / 100) * amount;
                total += serviceFee;
            }

            if (isUSD && this.billerPaymentSettings.pass_payment_processing_fee) {
                const processingFee = (parseFloat(this.feeVariables['BILLER-PAYMENT-PROCESSING-FEE']?.value || 0) / 100) * amount;
                total += processingFee;
            }
            if (!isUSD && this.billerPaymentSettings.pass_payment_processing_fee_zwg) {
                const processingFee = (parseFloat(this.feeVariables['BILLER-PAYMENT-PROCESSING-FEE-ZWG']?.value || 0) / 100) * amount;
                total += processingFee;
            }

            this.transactionFees = total;
        }
    },
    watch: {
        originalAmount: {
            handler: 'calculateTransactionFees',
            immediate: true
        },
        'form.payment': {
            handler: 'calculateTransactionFees',
            immediate: true
        },
        billerPaymentSettings: {
            handler: 'calculateTransactionFees',
            immediate: true
        },
        feeVariables: {
            handler: 'calculateTransactionFees',
            immediate: true
        }
    },
    beforeDestroy() {
        window.removeEventListener("scroll", this.handleScroll);
    },
    async mounted() {
        // Wait for next tick to ensure DOM is ready
        this.$nextTick(() => {
            window.addEventListener("scroll", this.handleScroll);
        });

        const billerId = this.decode(this.$route.params.id);
        this.getBillerBills(billerId);
    }
}
</script>

<template>
    <div id="content" class="bg-light-1 py-4">
        <div class="container">
            <div class="row">
                <div class="col-lg-6">
                    <div class="mb-3 d-flex align-items-center">

                        <img class="bill-img bill-img-lg"
                             :src="biller.image"
                             alt="">
                        <div class="ml-3">
                            <h5 class="font-weight-bold mb-0 biller-title">
                               {{ biller.name }}
                            </h5>
                        </div>
                    </div>
                    <img v-if="showDonationProgress" class="w-100 mb-2"
                             :src="biller.image"
                             alt="">

                    <div v-if="showDonationProgress" class="card shadow-sm border-0 mb-4">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="font-weight-bold  mb-0">
                                    Donation Progress
                                </h6>
                                <span class="badge badge-info">
                                    {{ Math.round(donationProgressPercentage) }}% Complete
                                </span>
                            </div>

                            <div class="progress mb-3" style="height: 20px;">
                                <div
                                    class="progress-bar progress-bar-striped progress-bar-animated bg-success"
                                    role="progressbar"
                                    :style="{ width: donationProgressPercentage + '%' }"
                                    :aria-valuenow="donationProgressPercentage"
                                    aria-valuemin="0"
                                    aria-valuemax="100">
                                </div>
                            </div>

                            <div class="row text-center mb-3">
                                <div class="col-6">
                                    <div class="text-muted small">Collected</div>
                                    <div class="font-weight-bold text-4 text-success">
                                        ${{ formatDonationAmount(donationProgress.amountCollected) }}
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="text-muted small">Goal</div>
                                    <div class="font-weight-bold text-4 text-primary">
                                        ${{ formatDonationAmount(donationProgress.targetAmount) }}
                                    </div>
                                </div>
                            </div>

                            <!-- Donation Breakdown -->
                            <div v-if="biller.usd_donations !== undefined || biller.zwg_donations !== undefined" class="border-top pt-3">
                                <div class="text-center mb-2">
                                    <small class="text-muted font-weight-bold">Total Donations Received</small>
                                </div>
                                <div class="row text-center">
                                    <div class="col-6">
                                        <div class="text-muted small">USD Donations</div>
                                        <div class="font-weight-bold text-primary">
                                            ${{ formatDonationAmount(biller.usd_donations) }}
                                        </div>
                                        <div class="text-muted" style="font-size: 10px;">
                                            {{ biller.usd_payments_count || 0 }} Donation(s)
                                        </div>
                                    </div>
                                    <div class="col-6">
                                        <div class="text-muted small">ZWG Donations</div>
                                        <div class="font-weight-bold text-warning">
                                            ZWG {{ formatDonationAmount(biller.zwg_donations) }}
                                        </div>
                                        <div class="text-muted" style="font-size: 10px;">
                                            {{ biller.zwg_payments_count || 0 }} Donation(s)
                                        </div>
                                    </div>
                                </div>
                                <!--<div v-if="biller.exchange_rate" class="text-center mt-2">
                                    <small class="text-muted">
                                        Exchange Rate: 1 USD = {{ biller.exchange_rate }} ZWG
                                    </small>
                                </div>-->
                            </div>

                            <div class="text-center mt-1">
                                <p class="" v-if="donationProgress.amountCollected >= donationProgress.targetAmount">
                                    Target reached! Thank you for your support!
                                </p>
                                <p class="" v-else>
                                    ${{ formatDonationAmount(donationProgress.targetAmount - donationProgress.amountCollected) }} remaining to reach our goal
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="blog-post card shadow-sm border-0 mb-4">
                        <div class="pt-4 pb-0 px-4">
                            <h5 class="mb-0 text-black font-weight-bold text-uppercase">
                                Pay {{ biller.name }}
                            </h5>
                            <small>{{ biller.category }}</small>
                        </div>
                        <hr>
                        <form ref="form" v-on="formEvents">
                            <div class="px-4 py-2">
                                <div v-if="biller.display_image" class="w-50 mb-3">
                                    <img class="rounded-lg shadow-md" style="width: 100%; object-fit: cover; aspect-ratio: 1"
                                         :src="biller.display_image"
                                         alt="">
                                </div>
                                <div class="form-group mt-2">
                                    <label for="email" class="form-label">Reference/Account</label>
                                    <input v-model="form.reference" type="text" class="form-control " id="reference" required
                                           aria-describedby="ref" placeholder="Reference/Account">
                                    <div v-text="form.errors.get('reference')" class="text-danger"/>
                                    <ConsumerLookup
                                        v-if="biller.corporate_id && biller.name"
                                        :reference="form.reference"
                                        :corporateId="biller.corporate_id"
                                        :corporateName="biller.name"
                                        :key="form.reference"
                                        @found="disableManualEntry = true"
                                        @not-found="disableManualEntry = false"
                                        @has-lookup="hasLookup = $event"
                                    />
                                </div>

                                <div class="d-flex flex-row align-items-center justify-content-between" v-if="!disableManualEntry">
                                    <div class="form-group" :style="{ marginRight: '20px' }">
                                        <label for="name" class="form-label">Name</label>
                                        <input v-model="form.name" :disabled="hasLookup && !disableManualEntry" type="text" class="form-control" id="name" required
                                               aria-describedby="quantity" placeholder="Name">
                                        <div v-text="form.errors.get('name')" class="text-danger"/>
                                    </div>
                                    <div class="form-group">
                                        <label for="surname" class="form-label">Surname</label>
                                        <input v-model="form.surname" :disabled="hasLookup && !disableManualEntry" type="text" class="form-control" id="surname" required
                                               aria-describedby="quantity" placeholder="Surname">
                                        <div v-text="form.errors.get('surname')" class="text-danger"/>
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label>Phone Number</label>
                                    <phone-input  v-model="form.phone"></phone-input>
                                    <div v-text="form.errors.get('phone')" class="text-danger"/>
                                    <small>* Also used for notifications</small>
                                </div>


                                <div class="form-group">
                                    <label>Pay with</label>
                                    <div class="input-group">
                                        <div class="input-group-prepend">
                                    <span class="input-group-text input-icon">
                                        <i class="fa fa-credit-card"></i>
                                    </span>
                                        </div>
                                        <select v-model="form.payment" :disabled="hasLookup && !disableManualEntry" required
                                                :class="[ 'custom-select' , form.errors.get('payment') ? 'is-invalid' : '' ]">
                                            <option v-if="isEcocashZwgEnabled && biller.bank_currencies && biller.bank_currencies.includes('ZWG')" value="ecocash">EcoCash (ZWG)</option>
                                            <option v-if="isEcocashUsdEnabled && biller.bank_currencies && biller.bank_currencies.includes('USD')" value="ecocash-usd">EcoCash (USD)</option>
                                            <option v-if="isStripeEnabled && biller.bank_currencies && biller.bank_currencies.includes('USD')" value="stripe">International Card</option>
                                        </select>
                                    </div>
                                    <div v-text="form.errors.get('payment')" class="invalid-feedback"/>
                                </div>
                                <div class="form-group" v-if="form.payment === 'ecocash' || form.payment === 'ecocash-usd'">
                                    <label>EcoCash Phone Number</label>
                                    <phone-input v-model="form.ecocash" :disabled="hasLookup && !disableManualEntry" :required="form.payment === 'ecocash' || form.payment === 'ecocash-usd'" :countries="['ZW']"></phone-input>
                                    <div v-text="form.errors.get('ecocash')" class="text-danger"/>
                                </div>
                                <div class="form-group" v-if="form.payment">
                                    <label>Amount</label>
                                    <div class="input-group">
                                        <div class="input-group-prepend">
                                        <span class="input-group-text input-icon">
                                            <img :src="flags[methods[form.payment]]" class="currency-icon mr-1" alt="">
                                            {{ methods[form.payment] }}
                                        </span>
                                        </div>
                                        <input required min="0.01" step="any" type="number" v-model="originalAmount"
                                               :class="[ 'form-control' , form.errors.get('amount') ? 'is-invalid' : '' ]"
                                               placeholder="Amount">
                                    </div>
                                    <div v-text="form.errors.get('amount')" class="invalid-feedback bg-danger rounded-bottom text-white text-center text-1 mt-n1"/>
                                </div>

                                <div class="form-group">
                                    <label for="email" class="form-label">Delivery Email</label>
                                    <div class="input-group">
                                        <div class="input-group-prepend">
                                        <span class="input-group-text input-icon">
                                            <i class="fa fa-envelope"></i>
                                        </span>
                                        </div>
                                        <input type="email" v-model="form.email" :disabled="hasLookup && !disableManualEntry" value="email" required
                                               :class="['form-control', form.errors.get('email') ? 'is-invalid' : '']"
                                               placeholder="Email">
                                        <div v-text="form.errors.get('email')" class="invalid-feedback"/>
                                    </div>
                                </div>
                                <div class="form-group ">
                                    <label>Message  <small>(Optional)</small></label>
                                    <textarea v-model="form.message" :disabled="hasLookup && !disableManualEntry" class="form-control" placeholder="Enter a brief message/description"></textarea>
                                    <div v-text="form.errors.get('message')" class="text-danger font-12"/>
                                </div>
                                <div class="form-group ">
                                    <div class="d-flex align-items-center">
                                        <input type="checkbox" class="mr-2" id="checkterms" v-model="termsAccepted">
                                        <label class="mb-0" for="checkterms">I accept the <a target="_blank" href="/terms">Terms & Conditions</a></label>
                                    </div>
                                </div>

                            </div>
                            <div v-if="transactionFees > 0" class="alert alert-info text-center border rounded mx-4 mb-3">
                                <i class="fa fa-info-circle"></i>
                                <strong>Transaction Processing Fees: {{ money(transactionFees) }}</strong>

                            </div>
                            <div class="alert alert-success text-center border rounded mx-4 mb-3">
                                <i class="fa fa-info-circle"></i>
                                <strong>
                                    Paying {{ form.payment === 'ecocash' ? 'ZWG' : 'USD' }} {{ money(Number(originalAmount) + Number(transactionFees)) || 0.00}}
                                </strong>
                            </div>
                            <div class="form-group px-4 mb-3">
                                <div class="form-group">
                                    <button type="button"
                                            v-bind ="buttonValidity"
                                            :class="{ 'btn-loading': form.loading, ...buttonValidity.class }"
                                            @click="formVisibility ? submit() : (formVisibility = true)">
                                        Pay Bill
                                    </button>
                                </div>
                            </div>
                        </form>

                    </div>
                </div>
                <div class="col-lg-6">
                        <div
                            ref="stickyBlock"
                            class="sticky"
                            :class="{ 'is-sticky': isSticky, 'is-absolute': isAbsolute }"
                        >
                            <ad-carousel></ad-carousel>
                        </div>
                </div>

            </div>
        </div>
    </div>
</template>
<style scoped>
.sticky {
    position: sticky;
    top: 90px;
}

.is-sticky {
    position: fixed;
    top: 0;
}

.is-absolute {
    position: absolute;
    top: auto;
    bottom: 0;
}
.bill-img {
    height: 24px;
    width: calc(22px * 4);
    object-fit: contain;
}
.bill-img-sm {
    height: 24px;
    object-fit: contain;
}

.biller-title{
    font-size: 1rem;
}

.bill-img.bill-img-lg {
    height: 56px;
    width: auto;
    max-width: 64px;
    border: 1px #eee solid;
    padding: 0.4rem;
    object-fit: contain;
    background-color: rgba(255, 255, 255, 0.45);
    border-radius: 6px;
}
</style>
